import React from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import styled from 'styled-components';

import {
  conversionChartHeadersMap,
  conversionChartSizes,
  fabricChartHeadersMap,
  inseamChartInchesMap,
  sculptKnitChartSizes,
  seamlessChartSizes,
  sizeChartSizesCm,
  sizeChartSizesInches,
  sizeGuideHeadersMap,
  sizeChartDataSeamlessInches,
  sizeChartDataSeamlessCms,
} from '~/shared/utils/sizeConstants';
import {
  desktop,
  mobile,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { useIntl, FormattedMessage } from '~/techstyle-shared/react-intl';

import { SizeGrid } from '../SizeGrid/SizeGrid';

import WomenFabricChart from './WomenFabricChart';

const WomenFitGuideWrapper = styled.div`
  color: ${({ theme }) => theme.colors.fitGuideDefault};
  line-height: 1;
  padding: 40px 16px;
`;

const MainHeader = styled.h2`
  font-size: 24px;
  font-weight: 700;
  letter-spacing: ${-0.5 / 24}em;
  text-align: center;
  margin-bottom: 16px;
`;

const Header = styled.h2`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: ${-0.4 / 16}em;
  text-align: left;
  margin-bottom: 12px;

  ${desktop`
     margin-bottom: 16px;
  `}
`;

const FitGuideTabList = styled(TabList)`
  display: flex;
  flex-direction: row;
  list-style-type: none;
  justify-content: center;
  font-size: 16px;
  text-align: center;
  margin-bottom: 16px;
`;
FitGuideTabList.tabsRole = 'TabList';

const FitGuideTab = styled(Tab)`
  padding: 12px 24px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  &.react-tabs__tab--selected {
    color: ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => theme.colors.textDefault};
  }
`;
FitGuideTab.tabsRole = 'Tab';

const SizeChartWrapper = styled.div`
  width: 656px;
  ${mobile`
    width: 100%;
  `}
`;

const DisclosureText = styled.div`
  text-align: right;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.womenFitGuideDisclosureText};
  margin-bottom: 43px;
`;

const womensBottomId = config.get('public.productCategories.womensBottom');

export default function WomenFitGuide({ categoryIdList }) {
  const intl = useIntl();
  const sizeGuideHeaders = sizeGuideHeadersMap(intl);
  const conversionChartHeaders = conversionChartHeadersMap(intl);
  const fabricChartHeaders = fabricChartHeadersMap(intl);
  const inseamChartInches = inseamChartInchesMap(intl);
  const isWomenBottoms = categoryIdList.includes(womensBottomId);
  const productDetailFromCtx = useProductDetailContext();
  const membershipBrandId = productDetailFromCtx.product.membershipBrandId;
  const fabricSeamlessHeader =
    membershipBrandId === 2 ? sizeGuideHeaders : fabricChartHeaders;

  return (
    <WomenFitGuideWrapper>
      <MainHeader>
        <FormattedMessage
          id="site_pdp.product_guides_size_guide_title"
          defaultMessage="Size and Fabric Guides"
        />
      </MainHeader>

      <Tabs>
        {isWomenBottoms && (
          <FitGuideTabList>
            <FitGuideTab selectedTabClassName="is-selected">
              <FormattedMessage
                id="site_pdp.product_guides_size_guide"
                defaultMessage="Size Guide"
              />
            </FitGuideTab>
            <FitGuideTab>
              <FormattedMessage
                id="site_pdp.product_guides_fabric_guide"
                defaultMessage="Fabric Guide"
              />
            </FitGuideTab>
          </FitGuideTabList>
        )}

        <TabPanel data-autotag="pdp-guides--size-guide">
          <Header>
            <FormattedMessage
              id="site_pdp.product_guides_size_guide"
              defaultMessage="Size Guide"
            />
          </Header>
          <Tabs>
            <SizeGrid.TabList>
              <SizeGrid.Tab>
                <FormattedMessage
                  id="site_product.size_chart_units_inches"
                  defaultMessage="Inches"
                />
              </SizeGrid.Tab>
              <SizeGrid.Tab>
                <FormattedMessage
                  id="site_product.size_chart_units_centimeters"
                  defaultMessage="Centimeters"
                />
              </SizeGrid.Tab>
            </SizeGrid.TabList>
            <TabPanel>
              <SizeGrid
                columnHeadings={sizeGuideHeaders}
                rowData={sizeChartSizesInches}
              />
            </TabPanel>
            <TabPanel>
              <SizeGrid
                columnHeadings={sizeGuideHeaders}
                rowData={sizeChartSizesCm}
              />
            </TabPanel>
            <DisclosureText>
              <FormattedMessage
                id="size_guide.size_guide_varies"
                defaultMessage="Varies by item. Check Product Details."
              />
            </DisclosureText>
          </Tabs>

          <SizeChartWrapper>
            <Header>
              <FormattedMessage
                id="site_product.inseam_guidance"
                defaultMessage="Inseam Guidance"
              />
            </Header>
            <SizeGrid rowData={inseamChartInches} />
          </SizeChartWrapper>

          <SizeChartWrapper>
            <Header>
              {membershipBrandId === 2 ? (
                <FormattedMessage
                  id="size_guide.size_guide_seamless"
                  defaultMessage="Seamless (XS-5X)"
                />
              ) : (
                <FormattedMessage
                  id="size_guide.size_guide_fabric_title"
                  defaultMessage="Fabric Size Guide"
                />
              )}
            </Header>
            <Tabs>
              {membershipBrandId === 2 ? (
                <SizeGrid.TabList>
                  <SizeGrid.Tab>
                    <FormattedMessage
                      id="site_product.size_chart_units_inches"
                      defaultMessage="Inches"
                    />
                  </SizeGrid.Tab>
                  <SizeGrid.Tab>
                    <FormattedMessage
                      id="site_product.size_chart_units_centimeters"
                      defaultMessage="Centimeters"
                    />
                  </SizeGrid.Tab>
                </SizeGrid.TabList>
              ) : (
                <SizeGrid.TabList>
                  <SizeGrid.Tab>
                    <FormattedMessage
                      id="site_product.fabric_chart_seamless"
                      defaultMessage="Seamless"
                    />
                  </SizeGrid.Tab>
                  <SizeGrid.Tab>
                    <FormattedMessage
                      id="site_product.fabric_chart_sculptknit"
                      defaultMessage="SculptKnit"
                    />
                  </SizeGrid.Tab>
                </SizeGrid.TabList>
              )}
              <TabPanel>
                <SizeGrid
                  columnHeadings={fabricSeamlessHeader}
                  rowData={
                    membershipBrandId === 1
                      ? seamlessChartSizes
                      : sizeChartDataSeamlessInches
                  }
                />
              </TabPanel>
              <TabPanel>
                <SizeGrid
                  columnHeadings={fabricSeamlessHeader}
                  rowData={
                    membershipBrandId === 1
                      ? sculptKnitChartSizes
                      : sizeChartDataSeamlessCms
                  }
                />
              </TabPanel>
            </Tabs>
          </SizeChartWrapper>
          {membershipBrandId === 1 && (
            <SizeChartWrapper>
              <Header>
                <FormattedMessage
                  id="site_product.conversion_chart"
                  defaultMessage="Conversion Chart"
                />
              </Header>
              <SizeGrid
                columnHeadings={conversionChartHeaders}
                rowData={conversionChartSizes}
              />
            </SizeChartWrapper>
          )}
        </TabPanel>
        <TabPanel data-autotag="pdp-guides--fabric-guide">
          <WomenFabricChart />
        </TabPanel>
      </Tabs>
    </WomenFitGuideWrapper>
  );
}

WomenFitGuide.propTypes = {
  categoryIdList: PropTypes.array,
};
